import {Injectable} from "@angular/core";
import {Router} from "@angular/router";
import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  ISignUpResult,
} from "amazon-cognito-identity-js";
import {Observable} from "rxjs";
import {encryptStorage} from "src/app/auth/utils/storage";
import {environment} from "src/environments/environment";

import {
  showDefaultError,
  showError,
  showSucess,
} from "../utils/functions/notification.function";
import {CoreApiService} from "src/app/core/core-api.service";
import { UserType } from "../enum/investor-type.enum";

@Injectable({
  providedIn: "root",
})
export class CognitoService {
  private userPool: CognitoUserPool;
  private user: CognitoUser;

  constructor(private router: Router, private coreApiService: CoreApiService) {
    this.userPool = new CognitoUserPool(environment.cognito);
  }

  signIn(Username: string, Password: string) {
    return new Observable<string>((observer) => {
      const authenticationDetails = new AuthenticationDetails({
        Username,
        Password,
      });

      const user = new CognitoUser({Username, Pool: this.userPool});

      user.authenticateUser(authenticationDetails, {
        onSuccess: (res) => {
          const idToken = res.getIdToken();
          encryptStorage.clear();

          encryptStorage.setItem(
            "@user",
            idToken.payload.family_name
              ? idToken.payload.family_name
              : idToken.payload.name,
            false
          );
          encryptStorage.setItem("@email", idToken.payload.email, false);
          encryptStorage.setItem(
            "@emailVerified",
            idToken.payload.email_verified
          );
          encryptStorage.setItem("@documentoIdentificador", Username, false);

          const tipoUsuario = idToken.payload["custom:user_type"] || '';
          encryptStorage.setItem("@tipoUsuario", tipoUsuario, false);
          encryptStorage.setItem("@entidadeCNPJ", idToken.payload["custom:cnpj"], false);
          encryptStorage.setItem(
            "@telefone",
            idToken.payload.phone_number,
            false
          );
          encryptStorage.setItem(
            "@celular",
            idToken.payload["custom:cell_number"],
            false
          );
          encryptStorage.setItem("@authorization", idToken.getJwtToken());
          // encryptStorage.setItem("@session", res, false);

          user.setSignInUserSession(res);
          observer.next("");
          observer.complete();
        },
        newPasswordRequired: () => {
          observer.next("newPassword");
          observer.complete();
        },
        onFailure: (e: any) => {
          observer.error(this.translateCognitoError(e));
          return;
        },
      });
    });
  }

  signUp(
    Username: string,
    password: string,
    attributes: { [key: string]: string }
  ) {
    const atributeList = Object.keys(attributes).map(
      (key) => new CognitoUserAttribute({Name: key, Value: attributes[key]})
    );

    return new Observable<ISignUpResult>((observer) => {
      this.userPool.signUp(
        Username,
        password,
        atributeList,
        null,
        (err: Error | null, result: ISignUpResult) => {
          if (err) {
            observer.error(this.translateCognitoError(err));
            return;
          }

          observer.next(result);
          observer.complete();
        }
      );
    });
  }

  confirmAccount(token: string, newUsername?: string) {
    return new Observable((observer) => {
      if (!newUsername) {
        this.user = this.currentUser();
      } else {
        this.user = new CognitoUser({
          Username: newUsername,
          Pool: this.userPool,
        });
      }

      this.user.confirmRegistration(token, true, (e, res) => {
        if (e) {
          observer.error(this.translateCognitoError(e));
          return;
        }

        observer.next(res);
        observer.complete();
      });
    });
  }


  updateUserEmail2(newEmail: string) {
    this.user = this.currentUser();

    const attributeList = [
      {
        Name: "email",
        Value: newEmail
      }
    ];

    return new Observable((observer) => {
      if (!this.user) {
        observer.error(new Error("Usuário não autenticado"));
        return;
      }

      this.user.updateAttributes(attributeList, (err, result) => {
        if (err) {
          observer.error(this.translateCognitoError(err));
          return;
        }
        observer.next(result);
        observer.complete();
      });
    });
  }

  async checkUser(documentoIdentificador: string) {
    const myHeaders = new Headers();
    myHeaders.append("x-api-key", "VtdbC8CWd58Ps0quKyhtf6zyIVgFiypD23nKsFyl");

    return fetch(
      `https://acn44riag5.execute-api.us-east-1.amazonaws.com/v1/usuario/${documentoIdentificador}`,
      {
        method: "DELETE",
        redirect: "follow",
        headers: myHeaders,
      }
    )
      .then((response) => response.json())
      .then((result) => result)
      .catch((error) => error);
  }

  //Password
  changePassword(currentPassword: string, newPassword: string) {
    return new Observable<string>((observer) => {
      if (this.currentUser()) {
        this.user.changePassword(currentPassword, newPassword, (err, res) => {
          if (err) {
            observer.error(this.translateCognitoError(err));
            return;
          }

          observer.next(res);
          observer.complete();
        });
      }
    });
  }

  resetPassword(Username: string) {
    return new Observable<string>((observer) => {
      const user = new CognitoUser({
        Username,
        Pool: this.userPool,
      });

      user.forgotPassword({
        onSuccess: (res) => {
          observer.next(res);
          observer.complete();
        },
        onFailure: (err) => {
          observer.error(this.translateCognitoError(err));
          return;
        },
      });
    });
  }

  confirmPassword(Username: string, newPassword: string, token: string) {
    return new Observable<string>((observer) => {
      const user = new CognitoUser({
        Username,
        Pool: this.userPool,
      });

      user.confirmPassword(token, newPassword, {
        onSuccess: (res) => {
          observer.next(res);
          observer.complete();
        },
        onFailure: (err) => {
          observer.error(this.translateCognitoError(err));
          return;
        },
      });
    });
  }

  //E-mail
  resendEmailCode(newUsername?: string) {
    return new Observable((observer) => {
      if (!newUsername) {
        this.user = this.currentUser();
      } else {
        this.user = new CognitoUser({
          Username: newUsername,
          Pool: this.userPool,
        });
      }

      this.user.resendConfirmationCode((e: Error, res: any) => {
        if (e) {
          observer.error(this.translateCognitoError(e));
          return;
        }

        observer.next(res);
        observer.complete();
      });
    });
  }

  resendEmailCodePublic(username: string) {
    return new Observable((observer) => {
      const user = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });

      user.resendConfirmationCode((e, res) => {
        if (e) {
          observer.error(this.translateCognitoError(e));
          return;
        }
        observer.next(res);
        observer.complete();
      });

    });
  }

  updateUserEmail(newEmail: string) {
    this.user = this.currentUser();

    const attributeList = [
      {
        Name: "email",
        Value: newEmail
      }
    ];

    return new Observable((observer) => {
      if (!this.user) {
        observer.error(new Error("Usuário não autenticado"));
        return;
      }

      this.user.updateAttributes(attributeList, (err, result) => {
        if (err) {
          observer.error(this.translateCognitoError(err));
          return;
        }
        observer.next(result);
        observer.complete();
      });
    });
  }


  verifyNewEmail(code: string) {
    this.user = this.currentUser();

    return new Observable((observer) => {
      if (!this.user) {
        observer.error(new Error("Usuário não autenticado"));
        return;
      }

      this.user.verifyAttribute("email", code, {
        onSuccess: (res) => {
          observer.next(res);
          observer.complete();
        },
        onFailure: (err) => {
          observer.error(this.translateCognitoError(err));
        }
      });
    });
  }


  confirmEmailPublic(username: string, code: string) {
    return new Observable((observer) => {
      const user = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });

      user.confirmRegistration(code, true, (err, result) => {
        if (err) {
          observer.error(this.translateCognitoError(err));
          return;
        }
        observer.next(result);
        observer.complete();
      });
    });
  }

  confirmEmail(token: string) {
    return new Observable((observer) => {
      if (this.currentUser()) {
        this.user.verifyAttribute("email", token, {
          onSuccess(res) {
            observer.next(res);
            observer.complete();
          },
          onFailure(err) {
            observer.error(this.translateCognitoError(err));
            return;
          },
        });
      }
    });
  }

  //JwtToken
  refreshToken(): Observable<void> {
    return new Observable<void>((observer) => {
      if (!this.currentUser()) {
        observer.error("Usuário não autenticado");
        return;
      }

      this.user.getSession((err, session) => {
        if (err || !session) {
          observer.error(err || 'Sessão inválida');
          return;
        }

        this.user.refreshSession(session.getRefreshToken(),
          (err, newSession) => {
            if (err) {
              observer.error(this.translateCognitoError(err));
              return;
            }

            const idToken = newSession.getIdToken();
            encryptStorage.setItem("@tipoUsuario", idToken.payload['custom:user_type'] || UserType.Undefined, false);
            encryptStorage.setItem("@authorization", idToken.getJwtToken());
            observer.next();
            observer.complete();
          }
        );
      });
    });
  }

  logout() {
    this.user = this.userPool.getCurrentUser();
    if (this.user) {
      this.user.signOut();
      encryptStorage.clear();
      this.router.navigate(["login"]);
    } else {
      encryptStorage.clear();
      this.router.navigate(["login"]);
    }
  }

  private currentUser(): CognitoUser | null {
    this.user = this.userPool.getCurrentUser();

    if (!this.user) {
      return null;
    }

    this.user.getSession((err, session) => {
      if (err) {
        showError("", "Não foi possivel validar a sessão.");
        return null;
      }
    });

    return this.user;
  }

  private translateCognitoError(error: Error | null): Error {
    if (!!error) {
      const errorMessages: { [key: string]: string } = {
        UserNotFoundException: "Usuário não encontrado.",
        NotAuthorizedException: "Credenciais inválidas.",
        UsernameExistsException:
          "Usuário já cadastrado, faça o login e associe a sua empresa através do menu Meu Perfil.",
        InvalidPasswordException:
          "A senha não atende aos critérios de segurança.",
        CodeMismatchException: "O código de verificação está incorreto.",
        ExpiredCodeException: "O código de verificação expirou.",
        LimitExceededException: "Excedeu o número de tentativas permitidas.",
        PasswordResetRequiredException:
          "O usuário precisa redefinir a senha antes de continuar.",
        UserNotConfirmedException: "O usuário ainda não confirmou o registro.",
        ServiceUnavailableException:
          "O serviço está temporariamente indisponível.",
        InternalErrorException: "Ocorreu um erro interno no serviço.",
        CodeMismatchExpection: "O código de verificação está incorreto",
        UserLambdaValidationException: error.message,
        PasswordHistoryPolicyViolationException: "Escolha uma nova senha diferente das últimas que você usou."

      };

      error.message =
        errorMessages[error.name] || "Ocorreu um erro desconhecido.";

      return error;
    }

    error.name = "InvalidError";
    error.message = "Ocorreu um erro desconhecido.";

    return error;
  }

  private restoreSessionIfNeeded(): CognitoUser | null {
    let currentUser = this.userPool.getCurrentUser();

    if (!currentUser) {
      console.log(
        "Nenhum usuário atual encontrado. Verificando localStorage..."
      );

      // Recupera informações do localStorage
      const storedIdToken = localStorage.getItem(
        `CognitoIdentityServiceProvider.${environment.cognito.ClientId}.idToken`
      );
      const storedAccessToken = localStorage.getItem(
        `CognitoIdentityServiceProvider.${environment.cognito.ClientId}.accessToken`
      );
      const storedRefreshToken = localStorage.getItem(
        `CognitoIdentityServiceProvider.${environment.cognito.ClientId}.refreshToken`
      );
      const storedUsername = localStorage.getItem(
        `CognitoIdentityServiceProvider.${environment.cognito.ClientId}.LastAuthUser`
      );

      if (
        storedIdToken &&
        storedAccessToken &&
        storedRefreshToken &&
        storedUsername
      ) {
        console.log(
          "Tokens encontrados no localStorage. Restaurando sessão..."
        );

        // Reconstrói a sessão do Cognito
        const session = new CognitoUserSession({
          IdToken: new CognitoIdToken({IdToken: storedIdToken}),
          AccessToken: new CognitoAccessToken({
            AccessToken: storedAccessToken,
          }),
          RefreshToken: new CognitoRefreshToken({
            RefreshToken: storedRefreshToken,
          }),
        });

        // Configura o usuário
        currentUser = new CognitoUser({
          Username: storedUsername,
          Pool: this.userPool,
        });
        currentUser.setSignInUserSession(session);

        console.log(
          "Sessão restaurada com sucesso para o usuário:",
          storedUsername
        );
      } else {
        console.warn("Não há tokens ou usuário válido no localStorage.");
        return null;
      }
    }
    return currentUser;
  }

  update(attributes: { [key: string]: string }) {
    //Removendo atributos que não podem ser alterados
    const filtredsAttributes = Object.keys(attributes)
      .filter(key => (key !== "username" && key !== "custom:tipoUsuario"))
      .reduce<{ [key: string]: string }>((acc, key) => {
        acc[key] = attributes[key];
        return acc;
      }, {});

    return new Observable((observer) => {
      if (this.currentUser()) {
        const atributeList = Object.keys(filtredsAttributes).map((key) => ({
          Name: key,
          Value: attributes[key],
        }));
        // console.log("PAssando para o cognito assim :" + JSON.stringify(atributeList));
        this.user.updateAttributes(atributeList, (err, res) => {
          if (err) {
            console.log("Erro ao atualizar atributos no cognito:" + JSON.stringify(err));
            observer.error(this.translateCognitoError(err));
            return;
          }
          observer.next(res);
          observer.complete();
        });
      } else {
        console.log("Erro ao atualizar atributos no cognito, usuario não autenticado");
      }
    });
  }

  getUserAttributes(): Observable<{ [key: string]: string }> {
    return new Observable((observer) => {
      if (!this.currentUser()) {
        observer.error("Usuário não autenticado");
        return;
      }

      this.user.getUserAttributes((err, attributes) => {
        if (err) {
          observer.error(this.translateCognitoError(err));
          return;
        }
        const map = (attributes || []).reduce((acc, a) => {
          acc[a.getName()] = a.getValue();
          return acc;
        }, {} as { [key: string]: string });
        observer.next(map);
        observer.complete();
      });
    });
  }
}
